import React, { useCallback, useState } from 'react'

import { DialogContext } from '@/shared/components/Modal/DialogContext'
import { DialogComponentProps, DialogType } from '@/shared/components/Modal/models/DialogStructure'

type GlobalDialogProviderProps = {
  children: React.ReactNode
}

export const GlobalDialogProvider: React.FC<GlobalDialogProviderProps> = ({ children }) => {
  const [dialogs, setDialogs] = useState<DialogType[]>([])

  const openDialog = useCallback((Component: React.ComponentType<DialogComponentProps>, dialogProps: Omit<DialogComponentProps, 'open'>) => {
    const updatedProps: DialogComponentProps = {
      open: true,
      onClose: dialogProps.onClose,
      ...dialogProps,
    }

    const newDialog: DialogType = {
      Component,
      props: updatedProps,
    }

    setDialogs(prevDialogs => [
      ...prevDialogs,
      newDialog,
    ])
  }, [setDialogs])

  const closeDialog = useCallback(() => {
    // Set the open property of the last dialog to false to animate it out
    setDialogs(prevDialogs => {
      if (prevDialogs.length === 0) {
        return prevDialogs
      }

      // Update the last dialog's 'open' property to false
      const updatedDialogs = prevDialogs.map((dialog, index) => {
        if (index === prevDialogs.length - 1) {
          return {
            ...dialog,
            props: {
              ...dialog.props,
              open: false,
            },
          }
        }

        return dialog
      })

      return updatedDialogs
    })

    // Remove the last dialog from the state after the animation is complete
    setTimeout(() => {
      setDialogs(prevDialogs => prevDialogs.slice(0, prevDialogs.length - 1))
    }, 250)
  }, [setDialogs])

  const closeAllDialogs = useCallback(() => {
    setDialogs([])
  }, [setDialogs])

  return (
    <DialogContext.Provider value={{ dialogs, openDialog, closeDialog, closeAllDialogs }}>
      {children}
      {dialogs && dialogs.length > 0 && dialogs.map((dialog, index) => {
        const DialogComponent = dialog.Component
        const { onClose, ...dialogProps } = dialog.props

        return <DialogComponent
          key={index}
          {...dialogProps}
          onClose={() => {
            closeDialog()

            if (onClose) {
              onClose()
            }
          }}
        />
      })}
    </DialogContext.Provider>
  )
}
